import { ElementBuilderDomHTMLSymbol, ElementBuilderDomSVGSymbol, ElementBuilderIsElementSymbol, ElementBuilderKindSymbol, ElementBuilderMarkupHTMLSymbol, ElementBuilderMarkupSVGSymbol, ElementBuilderTargetSymbol, ElementDeclarationKindBySymbol, FragmentTypeSymbol, } from "./constants.js";
import { cx } from "./cx.js";
import { hx, isHtmxAttributeName } from "./Htmx.js";
import { markupTemplateTag } from "./markupTemplateTag.js";
import { cloneElementDeclaration, createEmptyElementDeclaration, createListenerDeclarationKey, createStringDocumentFragment, getElementDeclarationFromElementBuilder, getMutableElementDeclarationFromElementBuilder, isElementBuilderChildren, isElementDeclaration, isElementDeclarationAttributesInput, isElementDeclarationKeyInput, isElementDeclarationKindDom, isElementDeclarationPropsInput, isElementDeclarationRefInput, isListenerDeclaration, isRenderMode, } from "./utils/mod.js";
const FRAGMENT_PROP_NAME = "fragment";
var SpecialProperties;
(function (SpecialProperties) {
    SpecialProperties["EventListener"] = "$on";
    SpecialProperties["Key"] = "$key";
    SpecialProperties["Children"] = "$children";
    SpecialProperties["ChildrenShorthand"] = "$";
    SpecialProperties["InnerHTML"] = "$innerHTML";
    SpecialProperties["Props"] = "$props";
    SpecialProperties["Attrs"] = "$attrs";
    SpecialProperties["Ref"] = "$ref";
    SpecialProperties["RenderMode"] = "$renderMode";
    SpecialProperties["HtmxAttribute"] = "$hx";
    SpecialProperties["ClassNames"] = "$cx";
    /**
     * A blocked property to prevent the proxy from being
     * resolved like a promise.
     *
     * @remarks
     *
     * This is used to prevent the proxy being recognized
     * as a `PromiseLike` object, which would cause the `then`
     * method to be called, and the resulting "promise" is
     * never resolved.
     */
    SpecialProperties["Then"] = "then";
})(SpecialProperties || (SpecialProperties = {}));
function createElementBuilderFactory(kindSymbol) {
    const kind = ElementDeclarationKindBySymbol[kindSymbol];
    function elementBuilderFactory(input) {
        let target;
        if (isElementDeclaration(input)) {
            target = cloneElementDeclaration(input);
        }
        else {
            target = createEmptyElementDeclaration(input, kind);
        }
        /**
         * Handoff the element declaration to the next element builder.
         *
         * @remarks
         *
         * This function is used to safely mutate the element declaration object,
         * before returning the next element builder.
         *
         * A new element builder is created with the same target element declaration,
         * and the requested changes are applied to the element declaration object.
         * The next element builder is then returned.
         *
         * @param mutateElementDeclaration - A function that mutates the element declaration object.
         * @returns The next element builder.
         */
        // TODO: Rename to `updateElementDeclaration` for clarity.
        const handoffToElementBuilder = function handoffToElementBuilder(mutateElementDeclaration) {
            /**
             * This is the next element builder that will be returned.
             */
            const nextElementBuilder = elementBuilderFactory(target);
            /**
             * This is the next element declaration from the next element builder.
             * This object can be safely mutated with the requested changes,
             * before returning the next element builder.
             */
            const nextElementDeclaration = getElementDeclarationFromElementBuilder(nextElementBuilder);
            mutateElementDeclaration(nextElementDeclaration);
            return nextElementBuilder;
        };
        const elementBuilder = new Proxy({}, {
            get: (_fakeTarget, propertyName, _receiver) => {
                if (propertyName === ElementBuilderKindSymbol) {
                    return kindSymbol;
                }
                if (propertyName === ElementBuilderTargetSymbol) {
                    return target;
                }
                if (propertyName === ElementBuilderIsElementSymbol) {
                    return true;
                }
                if (propertyName === SpecialProperties.Then) {
                    // This is a blocked property to prevent the proxy from being
                    // resolved like a promise.
                    return undefined;
                }
                return (...args) => {
                    if (propertyName === SpecialProperties.EventListener) {
                        return addEventListenerToElementDeclaration(handoffToElementBuilder, 
                        // Type assertion to avoid unnecessary type checks
                        args);
                    }
                    const [value, ...rest] = args;
                    /**
                     * This is the next element builder that will be returned.
                     */
                    // TODO: Refactor to use `handoffToElementBuilder` instead.
                    const nextElementBuilder = elementBuilderFactory(target);
                    /**
                     * This is the next element declaration from the next element builder.
                     * This object can be safely mutated with the requested changes,
                     * before returning the next element builder.
                     */
                    // TODO: Refactor to use `handoffToElementBuilder` instead.
                    const nextElementDeclaration = getMutableElementDeclarationFromElementBuilder(nextElementBuilder);
                    if (propertyName === SpecialProperties.Children ||
                        propertyName === SpecialProperties.ChildrenShorthand) {
                        const firstChild = value == null ? [] : [value];
                        const otherChildren = rest;
                        const children = [...firstChild, ...otherChildren];
                        if (!isElementBuilderChildren(children)) {
                            throw new Error("Invalid child type");
                        }
                        nextElementDeclaration.children = children;
                    }
                    else if (propertyName === SpecialProperties.InnerHTML) {
                        if (typeof value !== "string") {
                            throw new Error("Invalid innerHTML type");
                        }
                        nextElementDeclaration.children = [
                            createStringDocumentFragment.fromString(value),
                        ];
                    }
                    else if (propertyName === SpecialProperties.RenderMode) {
                        if (!isRenderMode(value)) {
                            throw new Error("Invalid RenderMode type");
                        }
                        nextElementDeclaration.renderMode = value;
                    }
                    else if (propertyName === SpecialProperties.HtmxAttribute) {
                        if (!isHtmxAttributeName(value)) {
                            throw new Error("Unrecognized htmx attribute name. If you are certain this is a valid htmx attribute, you may use the `$attrs` method instead.");
                        }
                        const attr = hx(value, ...rest);
                        if (!nextElementDeclaration.attributes) {
                            nextElementDeclaration.attributes = {};
                        }
                        nextElementDeclaration.attributes[attr.name] = attr.value;
                    }
                    else if (propertyName === SpecialProperties.ClassNames) {
                        const firstArg = value == null ? [] : [value];
                        const args = [...firstArg, ...rest];
                        const elementPropName = isElementDeclarationKindDom(kind)
                            ? "className"
                            : "class";
                        nextElementDeclaration.props[elementPropName] =
                            cx(args);
                    }
                    else if (propertyName === SpecialProperties.Ref) {
                        if (!isElementDeclarationRefInput(value)) {
                            throw new Error("Invalid ref type");
                        }
                        nextElementDeclaration.ref = value;
                    }
                    else if (propertyName === SpecialProperties.Key) {
                        if (!isElementDeclarationKeyInput(value)) {
                            throw new Error(`Invalid key type: ${typeof value}`);
                        }
                        nextElementDeclaration.key = value;
                    }
                    else if (propertyName === SpecialProperties.Props) {
                        if (!isElementDeclarationPropsInput(value)) {
                            throw new Error(`Invalid props value type: ${typeof value}`);
                        }
                        Object.assign(nextElementDeclaration.props, value);
                    }
                    else if (propertyName === SpecialProperties.Attrs) {
                        // The behavior of this method changes based on the kind of element builder.
                        // If the element builder is a DOM element, the dictionary is set as `attributes`,
                        // otherwise, it's set as `props`.
                        if (isElementDeclarationKindDom(kind)) {
                            if (!isElementDeclarationAttributesInput(value)) {
                                throw new Error(`Invalid attrs value type: ${typeof value}`);
                            }
                            if (!nextElementDeclaration.attributes) {
                                nextElementDeclaration.attributes = {};
                            }
                            Object.assign(nextElementDeclaration.attributes, value);
                        }
                        else {
                            if (!isElementDeclarationPropsInput(value)) {
                                throw new Error(`Invalid props value type: ${typeof value}`);
                            }
                            Object.assign(nextElementDeclaration.props, value);
                        }
                    }
                    else {
                        nextElementDeclaration.props[propertyName] =
                            value;
                    }
                    return nextElementBuilder;
                };
            },
        });
        // Type assertion is needed because of the Proxy
        return elementBuilder;
    }
    return elementBuilderFactory;
}
function createElementBuilderProxy(kind) {
    return new Proxy(createElementBuilderFactory, {
        apply: (_target, _thisArg, args) => {
            if (kind === ElementBuilderMarkupHTMLSymbol ||
                kind === ElementBuilderMarkupSVGSymbol) {
                const [strings, ...values] = args;
                return markupTemplateTag(strings, ...values);
            }
            throw new Error("This function is not supported for this kind of element builder.");
        },
        get: (_target, propertyName, _receiver) => {
            if (propertyName === ElementBuilderKindSymbol) {
                return kind;
            }
            if (propertyName === ElementBuilderIsElementSymbol) {
                return false;
            }
            if (propertyName === FRAGMENT_PROP_NAME) {
                return createElementBuilderFactory(kind)(FragmentTypeSymbol);
            }
            const elementTagName = propertyName;
            return createElementBuilderFactory(kind)(elementTagName);
        },
    });
}
/**
 * Adds an event listener to an element declaration.
 */
function addEventListenerToElementDeclaration(updateElementDeclaration, args) {
    const [type, handler, options] = args;
    let listeners;
    if (isListenerDeclaration(type)) {
        listeners = {
            [createListenerDeclarationKey(type)]: type,
        };
    }
    else if (typeof type === "object" && "type" in type === false) {
        listeners = type;
    }
    else if (typeof type === "string" &&
        // Beyond strict type safety, this check is necessary to support
        // the case where `handler` is `undefined` due to an optional
        // handler not being provided.
        handler) {
        const listenerDeclaration = { type, handler, options };
        listeners = {
            [createListenerDeclarationKey(listenerDeclaration)]: listenerDeclaration,
        };
    }
    return updateElementDeclaration((elementDeclaration) => {
        elementDeclaration.listeners = Object.assign(elementDeclaration.listeners ?? {}, listeners);
    });
}
/**
 * A builder for DOM element declarations
 *
 * @example
 *
 * ```ts
 * import { dom } from "knyt";
 *
 * dom.button.type("button").$("Click me");
 * ```
 */
export const dom = createElementBuilderProxy(ElementBuilderDomHTMLSymbol);
/**
 * A builder for HTML declarations, offering a fluent API and template tag
 * usage for creating HTML declarations
 *
 * @example
 *
 * ```ts
 * import { html } from "knyt";
 *
 * html.div.class("container").$("Hello, World!");
 * html`<strong>Hello, World!</strong>`;
 * ```
 */
export const html = createElementBuilderProxy(ElementBuilderMarkupHTMLSymbol);
/**
 * A builder for SVG declarations, offering a fluent API and template tag
 * usage for creating SVG declarations
 *
 * @example
 *
 * ```ts
 * import { svg } from "knyt";
 *
 * svg.circle.cx(50).cy(50).r(40).fill("red");
 * svg`<circle cx="50" cy="50" r="40" fill="red" />`;
 * ```
 */
export const svg = createElementBuilderProxy(ElementBuilderMarkupSVGSymbol);
export function createDOMBuilder(tagName) {
    return createElementBuilderFactory(ElementBuilderDomHTMLSymbol)(tagName);
}
/**
 * Creates an element builder that sets props as attributes.
 *
 * The renderer will use the resulting element builder to create an element
 * with the given tag name, and the given props will be set as attributes.
 *
 * @internal scope: workspace
 */
// TODO: Rename for clarity?
export function createMarkupBuilder(tagName) {
    return createElementBuilderFactory(ElementBuilderMarkupHTMLSymbol)(tagName);
}
/**
 * Creates an element builder for an HTML element that sets props as attributes.
 *
 * The renderer will use the resulting element builder to create an HTML element
 * with the given tag name, and the given props will be set as attributes.
 *
 * Specifically, this function is used to create an element builder for HTML elements,
 * where `HTMLGlobalAttributes` are included in the props.
 *
 * @internal scope: workspace
 */
export function createHTMLBuilder(tagName) {
    return createMarkupBuilder(tagName);
}
/**
 * Creates an element builder for an unknown HTML element.
 *
 * @internal scope: workspace
 */
// TODO: Rename for conciseness
export function createCustomElementBuilder(unknownTagName) {
    return createDOMBuilder(unknownTagName);
}
